import * as React from 'react';

/**
 * Breadcrumb state management for the Admin Console.
 * Pages register breadcrumb segments dynamically based on loaded entity data.
 * The BreadcrumbProvider holds the current segments and exposes a setSegments function.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface BreadcrumbSegment {
  readonly label: string;
  readonly href?: string; // undefined = current page (non-interactive)
}

export interface BreadcrumbContextValue {
  readonly segments: readonly BreadcrumbSegment[];
  readonly setSegments: (segments: readonly BreadcrumbSegment[]) => void;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const BreadcrumbContext = React.createContext<BreadcrumbContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

interface BreadcrumbProviderProps {
  readonly children: React.ReactNode;
}

/**
 * BreadcrumbProvider manages the current breadcrumb segments.
 * Pages call setSegments to register their position in the hierarchy.
 */
export function BreadcrumbProvider({ children }: BreadcrumbProviderProps): React.JSX.Element {
  const [segments, setSegments] = React.useState<readonly BreadcrumbSegment[]>([]);

  const contextValue = React.useMemo(
    (): BreadcrumbContextValue => ({
      segments,
      setSegments,
    }),
    [segments],
  );

  return React.createElement(BreadcrumbContext.Provider, { value: contextValue }, children);
}

// ─── Consumer Hook ───────────────────────────────────────────────────────────

/**
 * Hook to access breadcrumb state and the setSegments function.
 * Must be used within a BreadcrumbProvider.
 */
export function useBreadcrumb(): BreadcrumbContextValue {
  const context = React.useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumb must be used within a BreadcrumbProvider');
  }
  return context;
}
